import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Address from "_shared/addresses/address";
import Guid from "_shared/guids/guid";
import CompanyProfileSchema, {
  CompanyProfileJSON,
  CompanyProfileParams,
} from "marketplace/companies/company-profile-schema";
import Entity from "_framework/entities/entity";

export default class CompanyProfile extends Entity<CompanyProfile, CompanyProfileJSON, CompanyProfileParams> {
  public static get EXAMPLE(): CompanyProfile {
    return new CompanyProfile({
      name: "Example Company",
      description: "An example company profile",
      location: Address.EXAMPLE,
      yearEstablished: 2000,
      website: "https://www.example.com",
      numberOfEmployees: 100,
      categories: ["Technology", "Software"],
      avatarId: Guid.generate(),
      contactUserId: Guid.generate(),
    });
  }

  public static create(values: CompanyProfileParams): CompanyProfile {
    const parsed = CompanyProfileSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid CompanyProfile parameters.");
    }
    return new CompanyProfile(parsed);
  }

  public static fromJSON(json: CompanyProfileJSON | null | undefined): CompanyProfile | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const params = CompanyProfileSchema.deserialize(json);
    return new CompanyProfile(params);
  }

  public readonly name?: string;
  public readonly description?: string;
  public readonly location?: Address;
  public readonly yearEstablished?: number;
  public readonly website?: string;
  public readonly numberOfEmployees?: number;
  public readonly categories: string[] = [];
  public readonly avatarId?: Guid;
  public readonly contactUserId?: Guid;

  public constructor(params: CompanyProfileParams) {
    super(params);
    this.name = params.name;
    this.description = params.description;
    this.location = params.location ? Address.create(params.location) : undefined;
    this.yearEstablished = params.yearEstablished;
    this.website = params.website;
    this.numberOfEmployees = params.numberOfEmployees;
    this.categories = params.categories ?? [];
    this.avatarId = params.avatarId;
    this.contactUserId = params.contactUserId;
  }

  public toJSON(): CompanyProfileJSON {
    return CompanyProfileSchema.serialize(this);
  }

  public valueOf(): CompanyProfileJSON {
    return this.toJSON();
  }

  public toString(): string {
    return `CompanyProfile { 
      name: ${this.name}
      description: ${this.description}
      location: ${this.location?.toString()}
      yearEstablished: ${this.yearEstablished}
      website: ${this.website?.toString()}
      numberOfEmployees: ${this.numberOfEmployees}
      categories: ${this.categories.toString()}
      avatarId: ${this.avatarId?.toString()}
      contactUserId: ${this.contactUserId?.toString()}
    }`;
  }

  isEqualTo(other: CompanyProfile | null | undefined): boolean {
    return Boolean(
      other &&
        this.name === other.name &&
        this.description === other.description &&
        this.location?.isEqualTo(other.location) &&
        this.yearEstablished === other.yearEstablished &&
        this.website === other.website &&
        this.numberOfEmployees === other.numberOfEmployees &&
        this.categories.length === other.categories.length &&
        this.categories.every((category) => other.categories.includes(category)) &&
        this.avatarId?.isEqualTo(other.avatarId) &&
        this.contactUserId?.isEqualTo(other.contactUserId)
    );
  }
}
